import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import type {
  CSSProperties,
  KeyboardEvent as ReactKeyboardEvent,
  ReactNode,
  PointerEvent as ReactPointerEvent,
  RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import { send } from '@actual-app/core/platform/client/connection';
import * as monthUtils from '@actual-app/core/shared/months';
import type { CategoryEntity } from '@actual-app/core/types/models';
import { keyframes } from '@emotion/css';

import {
  claseAutoPulsa,
  useTravesia,
} from '#components/mobile/travesia/TravesiaContext';
import { debeCerrarse } from '#components/mobile/ui/AsaHoja';
import { separarEmoji } from '#components/mobile/ui/emoji';
import { HojaAcciones } from '#components/mobile/ui/HojaAcciones';
import type { FilaAccion } from '#components/mobile/ui/HojaAcciones';
import { Icono } from '#components/mobile/ui/Icono';
import type { NombreIcono } from '#components/mobile/ui/Icono';
import {
  color,
  movimiento,
  num,
  radio,
  sombra,
} from '#components/mobile/ui/tokens';
import { PrivacyFilter } from '#components/PrivacyFilter';
import { useFormat } from '#hooks/useFormat';
import { useLocale } from '#hooks/useLocale';
import { useLocalPref } from '#hooks/useLocalPref';
import { useNavigate } from '#hooks/useNavigate';
import { useNotes } from '#hooks/useNotes';
import { useUndo } from '#hooks/useUndo';
import { pushModal } from '#modals/modalsSlice';
import { useDispatch } from '#redux';
import { registrarUso } from '#uso';

import { describe as describeExpression } from './assignExpression';
import { useAssignKeypad } from './AssignKeypadContext';
import type { KeypadKey } from './AssignKeypadContext';
import { botonesBorrador, datosConBorrador } from './borrador';
import type { BotonesBorrador } from './borrador';
import { botonTeclado, lineaTeclado } from './botonTeclado';
import {
  cuotaMensual,
  objetivoDesdePlantillas,
  opcionSuficiente,
} from './objetivos';
import {
  alternarEscala,
  altoPanelBase,
  altoTecla,
  bajadoTrasArrastre,
  ESCALA_TOPE,
  escalaMaxima,
  escalaTrasArrastre,
  fuenteTecla,
  limitarEscala,
  normalizarEscala,
} from './tamanoTeclado';
import { useDatosTeclado } from './useDatosTeclado';
import { rutaFicha } from './useFichaCategoria';
import { usePausarMes } from './useIgnorarMes';
import { plantillasDe } from './useTravesiaCategoria';

const entrarDesdeAbajo = keyframes({
  from: { transform: 'translateY(100%)' },
  to: { transform: 'translateY(0)' },
});

/** Por encima de la barra de pestañas (zIndex 100) y debajo de los modales. */
export const KEYPAD_Z_INDEX = 200;
const CHIP_HEIGHT = 36;
const GAP = 6;
/** Área táctil del tirador (la barrita visible es más fina). */
const ALTO_TIRADOR = 32;
/** Alto de la franja superior del tirador. */
const ALTO_FRANJA = 44;
/** Dos toques en el tirador separados menos de esto alternan el tamaño. */
const DOBLE_TOQUE_MS = 350;
/** Movimiento mínimo (px) para que un toque en el tirador cuente como arrastre. */
const UMBRAL_ARRASTRE = 4;
/** Milisegundos del panel bajando solo hasta salir al cerrarlo deslizando. */
const MS_SALIDA = 200;

type AssignKeypadProps = {
  /** «Detalles» (en «⋯»): abre la ficha de la categoría. */
  onEditCategory: (id: CategoryEntity['id']) => void;
};

/**
 * Panel fijo en la parte inferior de la pestaña Presupuesto (móvil), al
 * estilo de la calculadora de YNAB: la lista sigue visible y se puede
 * desplazar; la fila seleccionada enseña la expresión mientras se teclea.
 */
export function AssignKeypad({ onEditCategory }: AssignKeypadProps) {
  const keypad = useAssignKeypad();
  const { t } = useTranslation();
  const panelRef = useRef<HTMLDivElement>(null);

  const isOpen = keypad?.selectedCategory != null;
  const setPanelHeight = keypad?.setPanelHeight;

  // Tamaño elegido con el tirador: escala de las teclas, recordada en este
  // dispositivo. Mientras se arrastra se usa `escalaArrastre` y solo se
  // guarda al soltar.
  const [escalaGuardada, setEscalaGuardada] = useLocalPref(
    'mobile.escalaTeclado',
  );
  const [escalaArrastre, setEscalaArrastre] = useState<number | null>(null);
  // Alto que tendría el panel a escala 1 (se deduce de lo que mide).
  const [altoBase, setAltoBase] = useState(0);
  const maximo =
    altoBase > 0
      ? escalaMaxima({ altoBase, altoVentana: window.innerHeight })
      : ESCALA_TOPE;
  const escala = limitarEscala(
    escalaArrastre ?? normalizarEscala(escalaGuardada),
    maximo,
  );
  // La medida del ResizeObserver corresponde a la escala ya pintada.
  const escalaPintadaRef = useRef(escala);
  useLayoutEffect(() => {
    escalaPintadaRef.current = escala;
  }, [escala]);

  // Mide el panel para que la lista deje sitio al final y la fila
  // seleccionada se pueda ver encima del panel.
  useLayoutEffect(() => {
    if (!setPanelHeight) {
      return;
    }
    if (!isOpen) {
      setPanelHeight(0);
      return;
    }
    const element = panelRef.current;
    if (!element) {
      return;
    }
    // Cuando el tirador cambia el tamaño, el ResizeObserver vuelve a medir:
    // la lista y los avisos usan siempre el alto real del panel.
    const measure = () => {
      const alto = element.getBoundingClientRect().height;
      setPanelHeight(alto);
      setAltoBase(altoPanelBase(alto, escalaPintadaRef.current));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [isOpen, setPanelHeight]);

  const selectedId = keypad?.selectedCategory?.id;
  const panelHeight = keypad?.panelHeight ?? 0;

  // Mientras el panel está abierto, la barra de pestañas flotante se oculta:
  // si no, tapa la última fila del teclado (0, ⌫, ✕, Hecho) en 390×844.
  useEffect(() => {
    if (panelHeight === 0) {
      return;
    }
    document.body.dataset.tecladoAbierto = '1';
    return () => {
      delete document.body.dataset.tecladoAbierto;
      // Red de seguridad: si algo desplazó el documento (el body no tiene
      // scroll), devolverlo arriba para que no quede la pantalla cortada.
      const doc = document.scrollingElement;
      if (doc && doc.scrollTop !== 0) {
        doc.scrollTop = 0;
      }
    };
  }, [panelHeight]);

  // Los avisos («Deshacer») salen arriba en el móvil (H30): el panel ya no
  // tiene que empujarlos.

  // El teclado forma parte de la pantalla: la lista tiene debajo un relleno
  // igual a su alto y la fila tocada se desplaza justo encima del panel (o
  // debajo de la cabecera si queda por arriba). Se espera a un fotograma para
  // medir con el relleno ya aplicado.
  useEffect(() => {
    if (!selectedId || panelHeight === 0) {
      return;
    }
    const frame = requestAnimationFrame(() => {
      const row = document.querySelector<HTMLElement>(
        `[data-category-id="${selectedId}"]`,
      );
      const panel = panelRef.current;
      if (!row || !panel) {
        return;
      }
      const scroller = getScrollParent(row);
      if (!scroller) {
        return;
      }
      const rowRect = row.getBoundingClientRect();
      // `offsetTop` no cuenta el `transform` de la animación de entrada.
      const panelTop =
        panel.offsetTop > 0
          ? panel.offsetTop
          : window.innerHeight - panelHeight;
      const scrollerTop = scroller.getBoundingClientRect().top;
      const margin = 8;
      if (rowRect.bottom > panelTop - margin) {
        scroller.scrollBy({
          top: rowRect.bottom - (panelTop - margin),
          behavior: 'smooth',
        });
      } else if (rowRect.top < scrollerTop + margin) {
        scroller.scrollBy({
          top: rowRect.top - (scrollerTop + margin),
          behavior: 'smooth',
        });
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [selectedId, panelHeight]);

  const tirador = useTirador({
    escala,
    maximo,
    panel: panelRef,
    // Bajarlo deslizando es como tocar fuera: guarda lo tecleado.
    onCerrar: () => keypad?.done(),
    onCambio: setEscalaArrastre,
    onFin: valor => {
      registrarUso('teclado_tamano', {
        escala: valor,
        alto: window.innerHeight,
      });
      setEscalaGuardada(valor);
      setEscalaArrastre(null);
    },
  });

  // Datos de uso: cada vez que se abre el teclado, con qué tamaño.
  useEffect(() => {
    if (isOpen) {
      registrarUso('teclado_abrir', { escala: escalaPintadaRef.current });
    }
  }, [isOpen]);

  if (!keypad || !keypad.selectedCategory) {
    return null;
  }

  const alto = altoTecla(escala);
  const fuente = fuenteTecla(escala);

  const { selectedCategory, press, done, cancel } = keypad;

  const key = (
    label: ReactNode,
    value: KeypadKey,
    ariaLabel?: string,
    operador = false,
  ) => (
    <KeypadButton
      key={value}
      aria-label={ariaLabel}
      onPress={() => press(value)}
      data-testid={`keypad-${value}`}
      alto={alto}
      fuente={fuente}
      style={
        operador
          ? { backgroundColor: color.surface3, color: color.fg2 }
          : undefined
      }
    >
      {label}
    </KeypadButton>
  );

  // Portal a <body>: un ancestro con `transform` (animación de entrada de la
  // página) sería el bloque contenedor del `position: fixed` y el panel no
  // quedaría pegado a la ventana.
  return createPortal(
    <View
      innerRef={panelRef}
      role="group"
      aria-label={t('Assign keypad for {{categoryName}}', {
        categoryName: selectedCategory.name,
      })}
      data-testid="assign-keypad"
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: KEYPAD_Z_INDEX,
        gap: 8,
        padding: '8px 10px 12px',
        paddingBottom: 'calc(12px + env(safe-area-inset-bottom))',
        backgroundColor: color.surface2,
        borderRadius: '26px 26px 0 0',
        boxShadow: sombra.hoja,
        animation: `${entrarDesdeAbajo} ${movimiento.hoja}ms ${movimiento.muelle} backwards`,
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      }}
    >
      {/* Tirador: arrastrar hacia arriba estira el teclado (las teclas se
          hacen más altas) y hacia abajo lo vuelve a achicar; ya en tamaño
          normal, seguir bajando lo cierra, como las demás hojas. Doble toque
          alterna entre normal y máximo. El área táctil ocupa toda la franja
          (44 px de alto, nunca menos de 32) aunque la barrita sea fina. */}
      <View
        style={{
          position: 'relative',
          height: ALTO_FRANJA,
          marginTop: -8,
          marginBottom: -8,
        }}
      >
        <View
          role="slider"
          tabIndex={0}
          aria-label={t('Keypad size')}
          aria-orientation="vertical"
          aria-valuemin={100}
          aria-valuemax={Math.round(maximo * 100)}
          aria-valuenow={Math.round(escala * 100)}
          data-testid="keypad-tirador"
          {...tirador}
          style={{
            position: 'absolute',
            inset: 0,
            minHeight: ALTO_TIRADOR,
            alignItems: 'center',
            justifyContent: 'center',
            touchAction: 'none',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            cursor: 'ns-resize',
            outline: 'none',
            '&:focus-visible > div': { backgroundColor: color.accent },
          }}
        >
          <View
            style={{
              width: 40,
              height: 5,
              borderRadius: 3,
              backgroundColor: escalaArrastre != null ? color.fg3 : color.line2,
              transition: `background-color ${movimiento.pildora}ms`,
            }}
          />
        </View>
      </View>
      <AccionesTeclado
        category={selectedCategory}
        onEditCategory={onEditCategory}
      />
      <View
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
          gap: GAP,
        }}
      >
        {key('7', '7')}
        {key('8', '8')}
        {key('9', '9')}
        {key('−', '-', t('Subtract from assigned'), true)}
        {key('4', '4')}
        {key('5', '5')}
        {key('6', '6')}
        {key('+', '+', t('Add to assigned'), true)}
        {key('1', '1')}
        {key('2', '2')}
        {key('3', '3')}
        {key('=', '=', t('Equals'), true)}
        {/* ✕: cierra sin guardar (lo tecleado se descarta). «Hecho»,
          «atrás» o tocar fuera sí guardan. */}
        <KeypadButton
          aria-label={t('Cancel without saving')}
          onPress={() => {
            registrarUso('teclado_cancelar');
            cancel();
          }}
          data-testid="keypad-cancelar"
          alto={alto}
          fuente={fuente}
          style={{
            flexDirection: 'column',
            gap: 1,
            fontSize: 12,
            fontWeight: 800,
            color: color.fg2,
          }}
        >
          <Icono nombre="x" size={18} />
          <Trans>Close</Trans>
        </KeypadButton>
        {key('0', '0')}
        {key(
          <Icono nombre="bksp" size={22} />,
          'backspace',
          t('Delete last digit'),
        )}
        <KeypadButton
          variant="primary"
          onPress={done}
          data-testid="keypad-done"
          alto={alto}
          fuente={fuente}
          style={{
            fontSize: Math.round((16 * fuente) / 21),
            fontWeight: 800,
          }}
        >
          <Trans>Done</Trans>
        </KeypadButton>
      </View>
    </View>,
    document.body,
  );
}

type KeypadButtonProps = {
  children: ReactNode;
  onPress: () => void;
  variant?: 'normal' | 'primary';
  /** Alto de la tecla (px); crece al estirar el teclado. */
  alto: number;
  /** Tamaño de la cifra (px). */
  fuente: number;
  style?: CSSProperties;
  'aria-label'?: string;
  'data-testid'?: string;
};

function KeypadButton({
  children,
  onPress,
  variant = 'normal',
  alto,
  fuente,
  style,
  ...rest
}: KeypadButtonProps) {
  const primario = variant === 'primary';
  return (
    <Button
      variant="bare"
      bounce={false}
      onPointerDown={() => {
        if ('vibrate' in navigator) {
          navigator.vibrate(3);
        }
      }}
      onPress={onPress}
      style={({ isPressed }) => ({
        height: alto,
        minHeight: alto,
        padding: 0,
        borderRadius: radio.boton,
        fontSize: fuente,
        fontWeight: 600,
        backgroundColor: primario ? color.accent : color.surface,
        color: primario ? color.accentInk : color.fg,
        transform: isPressed ? 'scale(0.95)' : undefined,
        filter: isPressed ? 'brightness(0.92)' : undefined,
        transition: `transform ${movimiento.pulsar}ms ${movimiento.muelle}`,
        ...num,
        ...style,
      })}
      {...rest}
    >
      {children}
    </Button>
  );
}

type AccionesTecladoProps = {
  category: CategoryEntity;
  onEditCategory: (id: CategoryEntity['id']) => void;
};

/**
 * Parte de arriba del teclado (H28), igual en el Plan y en «Asignar el mes»:
 * encabezado (categoría, línea secundaria y cifra grande), fila fija
 * «Auto · Mover · Objetivo · ⋯» y el botón principal contextual con su nota
 * (`botonTeclado.ts`).
 */
function AccionesTeclado({ category, onEditCategory }: AccionesTecladoProps) {
  const keypad = useAssignKeypad();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const format = useFormat();
  const locale = useLocale();
  const { showUndoNotification } = useUndo();
  const categoryNotes = useNotes(category.id);
  // Primera travesía: «Asignar lo sugerido (X)» y un «Auto» que reparte todo.
  const travesia = useTravesia();

  const [autoAssignOpen, setAutoAssignOpen] = useState(false);
  const [masOpen, setMasOpen] = useState(false);

  const month = keypad?.month ?? '';
  const onBudgetAction = keypad?.onBudgetAction;
  const reset = keypad?.reset;

  const {
    datos: datosHoja,
    asignadoMesPasado,
    objetivosActivos,
  } = useDatosTeclado(category, month);
  const { alternar: alternarSuficiente } = usePausarMes(
    category.id,
    category.name,
    month,
  );
  // Modo borrador («Asignar el mes»): los datos como quedarían al guardar y,
  // en vez del botón principal, «Asignar X» y/o «Suficiente».
  const borrador = keypad?.borrador ?? null;
  const cambio = borrador?.cambioDe(category.id);
  const datos = borrador ? datosConBorrador(datosHoja, cambio) : datosHoja;
  const marcadaSuficiente = !!cambio?.suficiente;
  const botonesB = borrador
    ? botonesBorrador({ datos, asignadoMesPasado, marcadaSuficiente })
    : null;
  const opcion = opcionSuficiente(datos);
  // «Suficiente» a mano junto al botón principal (Vadik, 25-oct), no solo en
  // «⋯». En el borrador ya tiene su propio botón.
  // En la primera travesía no: allí se asigna lo sugerido.
  const conSuficiente = !borrador && opcion != null && !travesia?.textoSugerido;
  const boton = botonTeclado({
    datos,
    asignadoMesPasado,
    puedeCubrir: true,
    objetivosActivos,
  });
  const linea = lineaTeclado(datos, asignadoMesPasado);
  const mesPasado = month
    ? monthUtils.format(monthUtils.prevMonth(month), 'MMMM', locale)
    : '';
  const nombreMes = month ? monthUtils.format(month, 'MMMM', locale) : '';
  const importe = (valor: number) => format(valor, 'financial');

  // Junto a cada opción, lo que pondría (Vadik, 25-oct): el mes pasado se
  // sabe ya; los promedios se piden al abrir la hoja.
  const [promedios, setPromedios] = useState<Record<number, number>>({});
  const hojaAbierta = autoAssignOpen || masOpen;
  useEffect(() => {
    if (!hojaAbierta || borrador || !month) {
      return;
    }
    let vivo = true;
    for (const N of [3, 6, 12]) {
      void send('budget/n-month-avg-amount', {
        month,
        N,
        category: category.id,
      }).then(valor => {
        if (vivo) {
          setPromedios(prev => ({ ...prev, [N]: valor }));
        }
      });
    }
    return () => {
      vivo = false;
    };
  }, [hojaAbierta, borrador, month, category.id]);
  const valorOpcion: Record<string, string | undefined> = {
    'copy-single-last':
      asignadoMesPasado != null ? importe(asignadoMesPasado) : undefined,
    'set-single-3-avg':
      promedios[3] != null ? importe(promedios[3]) : undefined,
    'set-single-6-avg':
      promedios[6] != null ? importe(promedios[6]) : undefined,
    'set-single-12-avg':
      promedios[12] != null ? importe(promedios[12]) : undefined,
    // El objetivo del mes es lo que pone «Aplicar el objetivo»; en los de
    // saldo a largo plazo (`longGoal`) es la meta, no lo que se asigna.
    'apply-single-category-template': cantidadObjetivo(),
  };
  // Mensual: el objetivo del mes. Por fecha (`longGoal`, p. ej. 1.000 € para
  // diciembre): la cuota de cada mes para llegar (Vadik, 25-oct).
  function cantidadObjetivo(): string | undefined {
    if (datosHoja.goal == null) {
      return undefined;
    }
    if (!datosHoja.longGoal) {
      return importe(datosHoja.goal);
    }
    const objetivo = objetivoDesdePlantillas(
      plantillasDe(category.goal_def),
      categoryNotes,
    );
    if (!objetivo || objetivo === 'otro' || !('fecha' in objetivo)) {
      return undefined;
    }
    return importe(
      cuotaMensual(
        objetivo.importe,
        datosHoja.balance - datosHoja.budgeted,
        objetivo.fecha,
        month,
      ),
    );
  }

  const runAutoAssign = useCallback(
    (action: string, message: string) => {
      setAutoAssignOpen(false);
      setMasOpen(false);
      if (!onBudgetAction) {
        return;
      }
      // Lo tecleado deja de valer: la hoja pone el nuevo importe.
      reset?.();
      onBudgetAction(month, action, { category: category.id });
      showUndoNotification({
        message,
        ...(action === 'apply-single-category-template' && {
          pre: categoryNotes ?? undefined,
        }),
      });
    },
    [
      category.id,
      categoryNotes,
      month,
      onBudgetAction,
      reset,
      showUndoNotification,
    ],
  );

  const onAutoAssignSelect = useCallback(
    (name: string) => {
      const categoryName = category.name;
      if (borrador) {
        // En el borrador solo se ofrece «Copiar lo del mes pasado».
        setAutoAssignOpen(false);
        setMasOpen(false);
        if (name === 'copy-single-last') {
          reset?.();
          borrador.asignar(category, asignadoMesPasado ?? 0);
        }
        return;
      }
      switch (name) {
        case 'copy-single-last':
          runAutoAssign(
            name,
            t(
              "{{categoryName}} budget has been set to last month's budgeted amount.",
              { categoryName },
            ),
          );
          break;
        case 'set-single-3-avg':
        case 'set-single-6-avg':
        case 'set-single-12-avg':
          runAutoAssign(
            name,
            t(
              '{{categoryName}} budget has been set to {{months}} month average.',
              {
                categoryName,
                months:
                  name === 'set-single-3-avg'
                    ? 3
                    : name === 'set-single-6-avg'
                      ? 6
                      : 12,
              },
            ),
          );
          break;
        case 'apply-single-category-template':
          runAutoAssign(
            name,
            t('{{categoryName}} budget templates have been applied.', {
              categoryName,
            }),
          );
          break;
        default:
          throw new Error(`Unrecognized menu item: ${name}`);
      }
    },
    [asignadoMesPasado, borrador, category, reset, runAutoAssign, t],
  );

  const onMoveMoney = useMoveMoneyModal(category);
  const abrirObjetivo = () =>
    void navigate(rutaFicha(category.id, month, true));

  // Pone un importe concreto sin cerrar el teclado (con aviso de deshacer).
  const asignar = (amount: number) => {
    if (!onBudgetAction) {
      return;
    }
    reset?.();
    onBudgetAction(month, 'budget-amount', { category: category.id, amount });
    showUndoNotification({
      message: t('{{categoryName}} budget has been updated to {{amount}}.', {
        categoryName: category.name,
        amount: importe(amount),
      }),
    });
  };

  const onPrincipal = () => {
    if (!boton || !onBudgetAction) {
      return;
    }
    switch (boton.tipo) {
      case 'cubrir':
        // La hoja «Cubrir» de dos toques, ya en «¿De dónde sale?». El
        // teclado sigue abierto; lo tecleado deja de valer.
        reset?.();
        dispatch(
          pushModal({
            modal: {
              name: 'cubrir',
              options: {
                month,
                onBudgetAction,
                categoriaInicial: category.id,
              },
            },
          }),
        );
        break;
      case 'asignar-falta':
        asignar(datos.budgeted + boton.importe);
        break;
      case 'como-mes-pasado':
        asignar(boton.importe);
        break;
      case 'crear-objetivo':
        abrirObjetivo();
        break;
      default:
        break;
    }
  };

  const onMas = (name: string) => {
    setMasOpen(false);
    switch (name) {
      case 'copy-single-last':
      case 'set-single-3-avg':
        onAutoAssignSelect(name);
        break;
      case 'movimientos':
        void navigate(`/categories/${category.id}?month=${month}`);
        break;
      case 'details':
        onEditCategory(category.id);
        break;
      case 'ignorar':
        if (borrador && !datosHoja.ignorada) {
          reset?.();
          borrador.marcarSuficiente(category, !marcadaSuficiente);
          break;
        }
        alternarSuficiente();
        break;
      default:
        break;
    }
  };

  // Cifra grande: lo asignado, o lo que se está tecleando.
  const display = keypad
    ? describeExpression(keypad.expression, keypad.liveBudgeted)
    : { primary: datos.budgeted, secondary: null };
  const enRojo = datos.balance < 0;

  const chipStyle = ({ isPressed }: { isPressed: boolean }): CSSProperties => ({
    minWidth: 0,
    height: CHIP_HEIGHT,
    minHeight: CHIP_HEIGHT,
    borderRadius: radio.boton,
    padding: '0 4px',
    gap: 6,
    fontSize: 13,
    fontWeight: 700,
    whiteSpace: 'nowrap',
    backgroundColor: color.surface,
    color: color.fg2,
    transform: isPressed ? 'scale(0.96)' : undefined,
    transition: `transform ${movimiento.pulsar}ms ${movimiento.muelle}, background-color ${movimiento.pildora}ms`,
  });
  const icono = { color: color.accent, flexShrink: 0 };
  // Mover escribe al momento en dos categorías: en el borrador no se ofrece.
  const conMover = !borrador;

  const textoLinea =
    linea.tipo === 'disponible'
      ? t('Available {{amount}}', { amount: importe(linea.importe) })
      : linea.tipo === 'objetivo'
        ? t('Target {{amount}} / month', { amount: importe(linea.importe) })
        : linea.mesPasado != null
          ? t('No target · {{month}}: {{amount}}', {
              month: mesPasado,
              amount: importe(linea.mesPasado),
            })
          : t('No target');

  let textoBoton: string | null = null;
  let nota = '';
  let iconoBoton: NombreIcono = 'check';
  if (boton) {
    switch (boton.tipo) {
      case 'cubrir':
        textoBoton = t('Cover {{amount}}', { amount: importe(boton.importe) });
        nota = t('Comes from another category or from "To Budget"');
        iconoBoton = 'alert';
        break;
      case 'asignar-falta':
        textoBoton = travesia?.textoSugerido
          ? t('Assign the suggested amount ({{amount}})', {
              amount: importe(boton.importe),
            })
          : t("Assign what's missing ({{amount}})", {
              amount: importe(boton.importe),
            });
        nota = t('Will be {{amount}} · target met', {
          amount: importe(boton.objetivo),
        });
        break;
      case 'como-mes-pasado':
        textoBoton = t('Same as last month ({{amount}})', {
          amount: importe(boton.importe),
        });
        nota = t('Repeats what was assigned in {{month}}', {
          month: mesPasado,
        });
        iconoBoton = 'repeat';
        break;
      case 'crear-objetivo':
        textoBoton = t('Create target');
        iconoBoton = 'target';
        break;
      default:
        break;
    }
  }

  return (
    <>
      {/* Encabezado: categoría (con su emoji), línea secundaria y cifra. */}
      <View
        data-testid="keypad-encabezado"
        style={{
          flexDirection: 'row',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 8,
          padding: '0 6px',
        }}
      >
        <View style={{ minWidth: 0, flexShrink: 1 }}>
          <Text
            style={{
              fontSize: 14,
              fontWeight: 800,
              color: color.fg,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {category.name}
          </Text>
          <Text
            data-testid="keypad-linea"
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: linea.tipo === 'disponible' ? color.bad : color.fg2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              ...num,
            }}
          >
            {textoLinea}
          </Text>
        </View>
        <View style={{ alignItems: 'flex-end', flexShrink: 0 }}>
          <PrivacyFilter>
            <Text
              data-testid="keypad-cifra"
              style={{
                fontSize: 24,
                fontWeight: 800,
                letterSpacing: '-0.03em',
                color: enRojo ? color.bad : color.fg,
                ...num,
              }}
            >
              {importe(display.primary)}
            </Text>
          </PrivacyFilter>
          {display.secondary && (
            <Text
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: color.accent,
                ...num,
              }}
            >
              {display.secondary.op === '+' ? '+' : '−'}
              {importe(display.secondary.amount)}
            </Text>
          )}
        </View>
      </View>

      {/* Fila fija de atajos: Auto · Mover · Objetivo · ⋯ */}
      <View
        style={{
          display: 'grid',
          gridTemplateColumns: conMover ? '1fr 1fr 1fr 44px' : '1fr 1fr 44px',
          gap: GAP,
        }}
      >
        <Button
          variant="bare"
          style={chipStyle}
          className={travesia?.autoPulsa ? claseAutoPulsa : undefined}
          onPress={() => {
            const onAuto = travesia?.onAuto;
            if (onAuto) {
              // En la travesía «Auto» reparte todo el dinero y cierra el
              // teclado (lo tecleado no se guarda).
              keypad?.cancel();
              void onAuto();
              return;
            }
            setAutoAssignOpen(true);
          }}
          data-testid="keypad-auto-assign"
          aria-label={t('Auto-assign')}
        >
          <Icono nombre="zap" size={16} style={icono} />
          <Trans>Auto</Trans>
        </Button>
        {autoAssignOpen && (
          <HojaAcciones
            titulo={separarEmoji(category.name).resto}
            emoji={separarEmoji(category.name).emoji}
            subtitulo={t('Auto-assign')}
            onCerrar={() => setAutoAssignOpen(false)}
            data-testid="keypad-auto-assign-hoja"
            filas={opcionesHoja(
              [
                {
                  name: 'copy-single-last',
                  text: t("Copy last month's budget"),
                },
                ...(borrador
                  ? []
                  : [
                      {
                        name: 'set-single-3-avg',
                        text: t('Set to 3 month average'),
                      },
                      {
                        name: 'set-single-6-avg',
                        text: t('Set to 6 month average'),
                      },
                      {
                        name: 'set-single-12-avg',
                        text: t('Set to yearly average'),
                      },
                    ]),
                ...(objetivosActivos && !borrador
                  ? [
                      {
                        name: 'apply-single-category-template',
                        text: t('Overwrite with template'),
                      },
                    ]
                  : []),
              ],
              onAutoAssignSelect,
              valorOpcion,
            )}
          />
        )}
        {conMover && (
          <Button
            variant="bare"
            style={chipStyle}
            onPress={onMoveMoney}
            data-testid="keypad-move-money"
            aria-label={t('Move money')}
          >
            <Icono nombre="move" size={16} style={icono} />
            <Trans>Move</Trans>
          </Button>
        )}
        <Button
          variant="bare"
          style={chipStyle}
          onPress={abrirObjetivo}
          data-testid="keypad-objetivo"
        >
          <Icono nombre="target" size={16} style={icono} />
          <Trans>Target</Trans>
        </Button>
        <Button
          variant="bare"
          style={chipStyle}
          onPress={() => setMasOpen(true)}
          data-testid="keypad-mas"
          aria-label={t('More keypad options')}
        >
          <Icono nombre="more" size={18} style={icono} />
        </Button>
        {masOpen && (
          <HojaAcciones
            titulo={separarEmoji(category.name).resto}
            emoji={separarEmoji(category.name).emoji}
            onCerrar={() => setMasOpen(false)}
            data-testid="keypad-mas-hoja"
            filas={opcionesHoja(
              [
                {
                  name: 'copy-single-last',
                  text: t("Copy last month's budget"),
                },
                ...(borrador
                  ? []
                  : [
                      {
                        name: 'set-single-3-avg',
                        text: t('Set to 3 month average'),
                      },
                    ]),
                { name: 'movimientos', text: t('See transactions') },
                { name: 'details', text: t('Details'), grupoNuevo: true },
                // «Suficiente» (= snooze de YNAB): solo si al objetivo le falta
                // dinero este mes, o para quitar la marca.
                ...(opcion === 'poner'
                  ? [
                      {
                        name: 'ignorar',
                        text: t('Enough this month ({{month}})', {
                          month: nombreMes,
                        }),
                      },
                    ]
                  : opcion === 'quitar'
                    ? [{ name: 'ignorar', text: t('Remove "enough"') }]
                    : []),
              ],
              onMas,
              valorOpcion,
            )}
          />
        )}
      </View>

      {/* Modo borrador: «Asignar X» y/o «Suficiente» (solo el borrador). */}
      {botonesB && borrador && (
        <BotonesBorradorTeclado
          botones={botonesB}
          marcada={marcadaSuficiente}
          importe={importe}
          onAsignar={valor => {
            reset?.();
            borrador.asignar(category, valor);
          }}
          onSuficiente={() => {
            reset?.();
            borrador.marcarSuficiente(category, !marcadaSuficiente);
          }}
        />
      )}
      {/* Botón principal contextual (si toca) y su nota. */}
      {(!borrador ||
        // En el borrador, sin nada que asignar ni «Suficiente», como hoy:
        // «Crear objetivo».
        (boton?.tipo === 'crear-objetivo' &&
          !botonesB?.asignar &&
          !botonesB?.suficiente)) &&
        ((boton && textoBoton) || conSuficiente) && (
          <View style={{ gap: 4 }}>
            <View style={{ flexDirection: 'row', gap: GAP }}>
              {boton && textoBoton && (
                <Button
                  variant="bare"
                  onPress={onPrincipal}
                  data-testid="keypad-principal"
                  data-tipo={boton.tipo}
                  style={({ isPressed }) => ({
                    flex: 1,
                    minWidth: 0,
                    height: 52,
                    minHeight: 52,
                    borderRadius: radio.boton,
                    padding: '0 12px',
                    gap: 8,
                    fontSize: 16,
                    fontWeight: 800,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    backgroundColor:
                      boton.tipo === 'cubrir' ? color.bad : color.accent,
                    color: boton.tipo === 'cubrir' ? '#fff' : color.accentInk,
                    transform: isPressed ? 'scale(0.98)' : undefined,
                    transition: `transform ${movimiento.pulsar}ms ${movimiento.muelle}`,
                    ...num,
                  })}
                >
                  <Icono nombre={iconoBoton} size={18} />
                  <Text
                    style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    {conSuficiente && boton.tipo === 'asignar-falta'
                      ? t("What's missing · {{amount}}", {
                          amount: importe(boton.importe),
                        })
                      : textoBoton}
                  </Text>
                </Button>
              )}
              {conSuficiente && (
                <Button
                  variant="bare"
                  onPress={alternarSuficiente}
                  aria-pressed={opcion === 'quitar'}
                  data-testid="keypad-suficiente"
                  style={({ isPressed }) => ({
                    flex: boton && textoBoton ? undefined : 1,
                    flexShrink: 0,
                    height: 52,
                    minHeight: 52,
                    borderRadius: radio.boton,
                    padding: '0 14px',
                    gap: 6,
                    fontSize: 16,
                    fontWeight: 800,
                    whiteSpace: 'nowrap',
                    backgroundColor:
                      opcion === 'quitar' ? color.accentSoft : color.surface,
                    color: color.accent,
                    border: `1px solid ${opcion === 'quitar' ? color.accent : 'transparent'}`,
                    transform: isPressed ? 'scale(0.98)' : undefined,
                    transition: `transform ${movimiento.pulsar}ms ${movimiento.muelle}`,
                  })}
                >
                  <Icono
                    nombre={opcion === 'quitar' ? 'check' : 'moon'}
                    size={18}
                  />
                  <Trans>Enough</Trans>
                </Button>
              )}
            </View>
            {boton && textoBoton && nota && (
              <Text
                data-testid="keypad-nota"
                style={{
                  textAlign: 'center',
                  fontSize: 12,
                  fontWeight: 600,
                  color: color.fg2,
                  ...num,
                }}
              >
                {nota}
              </Text>
            )}
          </View>
        )}
    </>
  );
}

/**
 * «Mover dinero»: abre la hoja «Mover dinero» con esta categoría de origen
 * (H27). Lo usa el chip «Mover» del teclado (en el Plan y en «Asignar el mes»).
 */
function useMoveMoneyModal(category: CategoryEntity) {
  const keypad = useAssignKeypad();
  const dispatch = useDispatch();

  const month = keypad?.month ?? '';
  const onBudgetAction = keypad?.onBudgetAction;

  return useCallback(() => {
    if (!onBudgetAction) {
      return;
    }
    dispatch(
      pushModal({
        modal: {
          name: 'mover',
          options: { month, onBudgetAction, origen: category.id },
        },
      }),
    );
  }, [category.id, dispatch, month, onBudgetAction]);
}

type TiradorOpciones = {
  escala: number;
  maximo: number;
  /** El panel entero: se mueve con el dedo al bajarlo por debajo del normal. */
  panel: RefObject<HTMLDivElement | null>;
  /** Bajado lo bastante (o lanzado hacia abajo): se cierra. */
  onCerrar: () => void;
  /** Escala provisional mientras se arrastra. */
  onCambio: (escala: number) => void;
  /** Escala final (al soltar, con doble toque o con las flechas). */
  onFin: (escala: number) => void;
};

/**
 * Gestos del tirador con pointer events (táctil y ratón): el puntero queda
 * capturado para que el arrastre siga aunque el dedo salga de la barrita, y
 * `touch-action: none` (en el estilo) evita que el gesto desplace la página.
 */
function useTirador({
  escala,
  maximo,
  panel,
  onCerrar,
  onCambio,
  onFin,
}: TiradorOpciones) {
  const arrastre = useRef<{
    id: number;
    y: number;
    escala: number;
    maximo: number;
    movido: boolean;
    yAnterior: number;
    tAnterior: number;
    velocidad: number;
  } | null>(null);
  const ultimoToque = useRef(0);

  const escalaEn = (clientY: number) => {
    const a = arrastre.current;
    if (!a) {
      return escala;
    }
    return escalaTrasArrastre({
      escalaInicial: a.escala,
      desplazamiento: a.y - clientY,
      maximo: a.maximo,
    });
  };

  const bajadoEn = (clientY: number) => {
    const a = arrastre.current;
    return a
      ? bajadoTrasArrastre({
          escalaInicial: a.escala,
          desplazamiento: a.y - clientY,
        })
      : 0;
  };

  const moverPanel = (y: number, transicion?: string) => {
    const el = panel.current;
    if (!el) {
      return;
    }
    el.style.transition = transicion ?? 'none';
    el.style.transform = y === 0 ? '' : `translateY(${y}px)`;
  };

  // Al soltar: si se bajó lo bastante sale hacia abajo y se cierra; si no,
  // vuelve a su sitio. Devuelve si se cierra.
  const soltarPanel = (bajado: number, velocidad: number) => {
    const el = panel.current;
    if (bajado <= 0 || !el) {
      return false;
    }
    if (!debeCerrarse(bajado, el.offsetHeight, velocidad)) {
      moverPanel(0, `transform ${movimiento.hoja}ms ${movimiento.suave}`);
      return false;
    }
    moverPanel(el.offsetHeight + 40, `transform ${MS_SALIDA}ms ease-in`);
    window.setTimeout(() => {
      onCerrar();
      // Si sigue abierto (p. ej. al guardar falló), vuelve a su sitio.
      window.setTimeout(() => {
        if (el.isConnected) {
          moverPanel(0, `transform ${movimiento.hoja}ms ${movimiento.suave}`);
        }
      }, 300);
    }, MS_SALIDA);
    return true;
  };

  return {
    onPointerDown: (e: ReactPointerEvent<HTMLDivElement>) => {
      if (e.pointerType === 'mouse' && e.button !== 0) {
        return;
      }
      e.preventDefault();
      e.currentTarget.setPointerCapture?.(e.pointerId);
      arrastre.current = {
        id: e.pointerId,
        y: e.clientY,
        escala,
        maximo,
        movido: false,
        yAnterior: e.clientY,
        tAnterior: e.timeStamp,
        velocidad: 0,
      };
    },
    onPointerMove: (e: ReactPointerEvent<HTMLDivElement>) => {
      const a = arrastre.current;
      if (!a || a.id !== e.pointerId) {
        return;
      }
      if (!a.movido && Math.abs(a.y - e.clientY) < UMBRAL_ARRASTRE) {
        return;
      }
      a.movido = true;
      const dt = Math.max(1, e.timeStamp - a.tAnterior);
      a.velocidad = (e.clientY - a.yAnterior) / dt;
      a.yAnterior = e.clientY;
      a.tAnterior = e.timeStamp;
      onCambio(escalaEn(e.clientY));
      moverPanel(bajadoEn(e.clientY));
    },
    onPointerUp: (e: ReactPointerEvent<HTMLDivElement>) => {
      const a = arrastre.current;
      if (!a || a.id !== e.pointerId) {
        return;
      }
      if (a.movido) {
        onFin(escalaEn(e.clientY));
        soltarPanel(bajadoEn(e.clientY), a.velocidad);
        arrastre.current = null;
        ultimoToque.current = 0;
        return;
      }
      arrastre.current = null;
      const ahora = e.timeStamp;
      if (ultimoToque.current && ahora - ultimoToque.current < DOBLE_TOQUE_MS) {
        ultimoToque.current = 0;
        onFin(alternarEscala(escala, maximo));
      } else {
        ultimoToque.current = ahora;
      }
    },
    onPointerCancel: (e: ReactPointerEvent<HTMLDivElement>) => {
      const a = arrastre.current;
      if (!a || a.id !== e.pointerId) {
        return;
      }
      const final = a.movido ? escalaEn(e.clientY) : escala;
      arrastre.current = null;
      moverPanel(0, `transform ${movimiento.hoja}ms ${movimiento.suave}`);
      onFin(final);
    },
    // Teclado: flechas para estirar o achicar, Inicio/Fin a los extremos.
    onKeyDown: (e: ReactKeyboardEvent<HTMLDivElement>) => {
      const paso = 0.1;
      const siguiente =
        e.key === 'ArrowUp' || e.key === 'ArrowRight'
          ? escala + paso
          : e.key === 'ArrowDown' || e.key === 'ArrowLeft'
            ? escala - paso
            : e.key === 'Home'
              ? 1
              : e.key === 'End'
                ? maximo
                : null;
      if (siguiente == null) {
        return;
      }
      e.preventDefault();
      onFin(limitarEscala(siguiente, maximo));
    },
  };
}

function getScrollParent(element: HTMLElement): HTMLElement | null {
  // El contenedor con scroll más cercano que de verdad se pueda desplazar; si
  // ninguno puede (todo cabe), el más cercano con scroll propio. Nunca el
  // documento: con `body { overflow: hidden }` un scroll programático del
  // documento no se puede deshacer a mano y deja un hueco abajo.
  let primero: HTMLElement | null = null;
  let node: HTMLElement | null = element.parentElement;
  while (node && node !== document.body) {
    const { overflowY } = getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll') {
      if (node.scrollHeight > node.clientHeight) {
        return node;
      }
      primero ??= node;
    }
    node = node.parentElement;
  }
  return primero;
}

type BotonesBorradorTecladoProps = {
  botones: BotonesBorrador;
  marcada: boolean;
  importe: (valor: number) => string;
  onAsignar: (valor: number) => void;
  onSuficiente: () => void;
};

/**
 * Botones verdes del teclado en «Asignar el mes» (modo borrador): «Asignar
 * X» (lo previsto) y/o «Suficiente» (marca «suficiente» el mes al guardar). Solo tocan
 * el borrador; nada se guarda hasta «Guardar presupuesto».
 */
export function BotonesBorradorTeclado({
  botones,
  marcada,
  importe,
  onAsignar,
  onSuficiente,
}: BotonesBorradorTecladoProps) {
  const { t } = useTranslation();
  const { asignar, suficiente } = botones;
  if (!asignar && !suficiente) {
    return null;
  }
  const estilo =
    (principal: boolean) =>
    ({ isPressed }: { isPressed: boolean }): CSSProperties => ({
      flex: 1,
      minWidth: 0,
      height: 52,
      minHeight: 52,
      borderRadius: radio.boton,
      padding: '0 12px',
      gap: 8,
      fontSize: 16,
      fontWeight: 800,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      backgroundColor: principal ? color.accent : color.surface,
      color: principal ? color.accentInk : color.accent,
      transform: isPressed ? 'scale(0.98)' : undefined,
      transition: `transform ${movimiento.pulsar}ms ${movimiento.muelle}`,
      ...num,
    });
  return (
    <View style={{ gap: 4 }}>
      <View style={{ flexDirection: 'row', gap: GAP }}>
        {asignar && (
          <Button
            variant="bare"
            onPress={() => onAsignar(asignar.importe)}
            data-testid="keypad-borrador-asignar"
            data-motivo={asignar.motivo}
            style={estilo(true)}
          >
            <Icono nombre="check" size={18} />
            <Text style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {t('Assign {{amount}}', { amount: importe(asignar.importe) })}
            </Text>
          </Button>
        )}
        {suficiente && (
          <Button
            variant="bare"
            onPress={onSuficiente}
            aria-pressed={marcada}
            data-testid="keypad-borrador-suficiente"
            style={estilo(!asignar && !marcada)}
          >
            <Icono nombre="check" size={18} />
            <Text style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              <Trans>Enough</Trans>
            </Text>
          </Button>
        )}
      </View>
      {marcada && (
        <Text
          data-testid="keypad-nota"
          style={{
            textAlign: 'center',
            fontSize: 12,
            fontWeight: 600,
            color: color.fg2,
          }}
        >
          {t('Marked enough this month when you save. Tap again to undo.')}
        </Text>
      )}
    </View>
  );
}

/** Icono de cada opción de las hojas del teclado. */
const ICONO_OPCION_TECLADO: Record<string, NombreIcono> = {
  'copy-single-last': 'cal',
  'set-single-3-avg': 'pie',
  'set-single-6-avg': 'pie',
  'set-single-12-avg': 'pie',
  'apply-single-category-template': 'target',
  movimientos: 'list',
  details: 'pencil',
  ignorar: 'check',
};

/** Pasa las opciones del teclado a filas de `HojaAcciones`. */
function opcionesHoja(
  opciones: Array<{ name: string; text: string; grupoNuevo?: boolean }>,
  onSelect: (name: string) => void,
  valores: Record<string, string | undefined> = {},
): FilaAccion[] {
  return opciones.map(o => ({
    id: o.name,
    texto: o.text,
    valor: valores[o.name],
    icono: ICONO_OPCION_TECLADO[o.name] ?? 'more',
    grupoNuevo: o.grupoNuevo,
    onPress: () => onSelect(o.name),
  }));
}
